import React from "react";
import { Divider, Flex, Grid, Text } from "@chakra-ui/react";
import StatusBadge from "@/shared/components/StatusBadge";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickOrderDetailInfoField from "@/features/pathpick/features/order-history/features/detail/components/PathPickOrderDetailInfoField";
import { DEFAULT_ORDER_STATUS_COLOR_VARIANT } from "@/features/pathpick/features/order-history/constants";
import {
  ORDER_DETAIL_KEY,
  SHIPMENT_STATUS_META,
} from "@/features/pathpick/features/order-history/features/detail/constants";
import { PathPickOrderShipmentView } from "@/features/pathpick/features/order-history/features/detail/types";
import { formatPathPickOrderDate } from "@/features/pathpick/features/order-history/utils";

interface Props {
  shipment: PathPickOrderShipmentView;
  index: number; // 1부터 시작하는 배송 번호
}

function PathPickOrderDetailShipmentCard({ shipment, index }: Props) {
  const { t } = useI18nHelper();
  // 서버가 아직 매핑되지 않은 상태를 내려주면 원본 코드를 회색 배지로 노출한다(주문 상태 배지와 동일).
  const statusMeta = SHIPMENT_STATUS_META[shipment.status];
  // 택배사/운송장은 배송 준비 단계에서 아직 비어 있다.
  const notRegistered = t(`${ORDER_DETAIL_KEY}shipping_not_registered`);

  return (
    <Flex
      flexDirection={"column"}
      gap={"12px"}
      padding={"12px 16px"}
      border={"0.5px solid #EFEFED"}
      borderRadius={"8px"}
    >
      <Flex justifyContent={"space-between"} alignItems={"center"} gap={"8px"}>
        <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
          {t(`${ORDER_DETAIL_KEY}shipping_group`, { index })}
        </Text>
        <StatusBadge
          status={statusMeta ? t(statusMeta.labelKey) : shipment.status}
          colorVariant={
            statusMeta?.colorVariant ?? DEFAULT_ORDER_STATUS_COLOR_VARIANT
          }
          size={"small"}
        />
      </Flex>

      <Grid
        templateColumns={{ base: "repeat(2, minmax(0, 1fr))", md: "1fr 1fr 1fr" }}
        gap={"12px"}
      >
        <PathPickOrderDetailInfoField
          label={t(`${ORDER_DETAIL_KEY}shipping_courier`)}
          value={shipment.courierName ?? notRegistered}
        />
        <PathPickOrderDetailInfoField
          label={t(`${ORDER_DETAIL_KEY}shipping_tracking`)}
          value={shipment.trackingNumber ?? notRegistered}
        />
        <PathPickOrderDetailInfoField
          label={t(`${ORDER_DETAIL_KEY}shipping_expected_delivery`)}
          value={
            shipment.expectedDeliveredOn
              ? formatPathPickOrderDate(shipment.expectedDeliveredOn)
              : notRegistered
          }
        />
      </Grid>

      <Divider borderColor={"#EFEFED"} />

      <Flex flexDirection={"column"} gap={"8px"}>
        {shipment.items.map((item) => (
          <Flex key={item.id} flexDirection={"column"} gap={"2px"}>
            <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
              {`${item.brandName} · ${item.sku}`}
            </Text>
            <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
              {`${item.productName ?? item.sku} · ${item.displayOptionName || item.optionName} · ${t(
                `${ORDER_DETAIL_KEY}shipment_confirmed_quantity`,
                { count: item.quantity }
              )}`}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
}

export default PathPickOrderDetailShipmentCard;
